import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiError,
  getActivityTypes,
  createActivity,
  uploadActivityImages,
  setImageVariants,
  activityImageUrl,
} from '../../api/client';
import FlikSelect from '../../components/Select/FlikSelect';
import ActivityTypeIcon from '../../activities/ActivityTypeIcon';
import VariantCropModal from '../../components/ImageVariants/VariantCropModal';
import './AddActivityModal.css';

/**
 * Normalises a field's backend-supplied choices into `{ value, label }` pairs
 * for ENUM fields. Accepts the common shapes a backend might
 * send: an array of strings, or an array of objects keyed by
 * value/label (`value`/`label`, `key`/`name`, `id`/`text`, …).
 */
function normalizeOptions(field) {
  const raw = field.options || field.choices || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((opt) => {
      if (opt === null || opt === undefined) return null;
      if (typeof opt === 'object') {
        const value = opt.value ?? opt.key ?? opt.id ?? opt.slug ?? opt.name;
        const label = opt.label ?? opt.name ?? opt.text ?? opt.title ?? value;
        if (value === undefined || value === null) return null;
        return { value: String(value), label: String(label) };
      }
      return { value: String(opt), label: String(opt) };
    })
    .filter(Boolean);
}

/**
 * "Add activity" dialog for ADMIN and EDITOR users.
 *
 * On open it loads the activity-type catalogue (GET /activity-types). Picking a
 * type reveals a form built dynamically from that type's field definitions
 * (system defaults such as latitude/longitude plus any admin-defined custom
 * fields), each rendered by its FieldType. Submitting POSTs the new activity and
 * hands it back to the parent through `onCreated`.
 */
function AddActivityModal({ onCreated, onClose }) {
  const [types, setTypes] = useState(null); // null = loading
  const [loadError, setLoadError] = useState('');
  const [typeSlug, setTypeSlug] = useState('');
  const [name, setName] = useState('');
  const [values, setValues] = useState({});
  const [images, setImages] = useState([]); // { file, url } picked before create
  const [pending, setPending] = useState(null); // { activity, queue } after upload
  const [variantsSaving, setVariantsSaving] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const nameRef = useRef(null);

  // Revoke any remaining object URLs on unmount so the previews don't leak.
  const imagesRef = useRef(images);
  imagesRef.current = images;
  useEffect(
    () => () => imagesRef.current.forEach((img) => URL.revokeObjectURL(img.url)),
    []
  );

  const addImages = (files) =>
    setImages((prev) => [
      ...prev,
      ...files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    ]);

  const removeImage = (url) =>
    setImages((prev) => {
      URL.revokeObjectURL(url);
      return prev.filter((img) => img.url !== url);
    });

  // Load the catalogue once.
  useEffect(() => {
    let alive = true;
    getActivityTypes()
      .then((list) => {
        if (!alive) return;
        // Unwrap a Spring Page defensively; otherwise expect a plain array.
        const arr = Array.isArray(list) ? list : list?.content || [];
        setTypes(arr);
        if (arr.length === 1) setTypeSlug(arr[0].slug);
      })
      .catch((err) => {
        if (!alive) return;
        setTypes([]);
        setLoadError(
          err instanceof ApiError
            ? err.detail || err.message
            : 'No hemos podido cargar los tipos de actividad.'
        );
      });
    return () => {
      alive = false;
    };
  }, []);

  // Lock background scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const selectedType = useMemo(
    () => (types || []).find((t) => t.slug === typeSlug) || null,
    [types, typeSlug]
  );

  // Reset the dynamic values whenever the chosen type changes.
  useEffect(() => {
    setValues({});
    setError('');
    if (typeSlug) nameRef.current?.focus();
  }, [typeSlug]);

  // Escalada types carry a UIAA→French grade table (`derivedMapping`) on the
  // UIAA field. Picking a UIAA grade fills in the French grade automatically and
  // the French field is locked so the two can't drift apart.
  const derivedMapping = useMemo(() => {
    const french = (selectedType?.fields || []).find((f) => f.key === 'frenchGrade');
    return french && typeof french.derivedMapping === 'object'
      ? french.derivedMapping
      : null;
  }, [selectedType]);

  const setValue = (key, value) =>
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'uiaaGrade' && derivedMapping) {
        next.frenchGrade = derivedMapping[value] ?? '';
      }
      return next;
    });

  // Coerces a raw form value into the shape the backend expects for its type,
  // dropping empties (so optional fields are simply omitted).
  const coerce = (field, raw) => {
    if (field.type === 'BOOLEAN') return Boolean(raw);
    if (raw === '' || raw === undefined || raw === null) return undefined;
    if (field.type === 'DOUBLE' || field.type === 'INTEGER') {
      const n = Number(raw);
      return Number.isNaN(n) ? undefined : n;
    }
    return raw;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedType) {
      setError('Escoge un tipo de actividad.');
      return;
    }
    if (!name.trim()) {
      setError('Ponle un nombre a la actividad.');
      return;
    }

    // Build the values map and check required fields client-side.
    const payloadValues = {};
    for (const field of selectedType.fields) {
      const coerced = coerce(field, values[field.key]);
      if (coerced === undefined) {
        if (field.required) {
          setError(`El campo "${field.label}" es obligatorio.`);
          return;
        }
        continue;
      }
      payloadValues[field.key] = coerced;
    }

    setSubmitting(true);
    setError('');
    try {
      let created = await createActivity({
        name: name.trim(),
        activityType: selectedType.slug,
        values: payloadValues,
      });
      // Images attach to an existing activity, so upload them once it exists.
      if (images.length) {
        created = await uploadActivityImages(
          created.id,
          images.map((img) => img.file)
        );
        // Every uploaded image needs its three display-variant crops. Hand off
        // to the variant cropper (which finishes the create when the queue is
        // empty) instead of closing now.
        const ids = created.imageIds || [];
        if (ids.length) {
          setPending({ activity: created, queue: ids });
          return;
        }
      }
      onCreated?.(created);
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido crear la actividad. Inténtalo de nuevo.'
      );
      setSubmitting(false);
    }
  };

  // Save one image's three variant crops, then advance the queue. When it drains,
  // the activity is fully created — report it and close.
  const saveVariants = async (variants) => {
    setVariantsSaving(true);
    setError('');
    try {
      const updated = await setImageVariants(
        pending.activity.id,
        pending.queue[0],
        variants
      );
      const rest = pending.queue.slice(1);
      if (rest.length) {
        setPending({ activity: updated, queue: rest });
      } else {
        onCreated?.(updated);
        onClose();
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido guardar las variantes.'
      );
    } finally {
      setVariantsSaving(false);
    }
  };

  // The activity already exists; finishing without variants just leaves them
  // unset (they can be added later from the admin panel).
  const finishWithoutVariants = () => {
    onCreated?.(pending.activity);
    onClose();
  };

  if (pending) {
    return (
      <VariantCropModal
        src={activityImageUrl(pending.queue[0])}
        title={`Variantes de la imagen (${pending.queue.length} pendiente${
          pending.queue.length > 1 ? 's' : ''
        })`}
        saving={variantsSaving}
        onCancel={finishWithoutVariants}
        onComplete={saveVariants}
      />
    );
  }

  return (
    <div className="Modal" role="presentation" onMouseDown={onClose}>
      <div
        className="Modal__card AddActivity"
        role="dialog"
        aria-modal="true"
        aria-label="Añadir actividad"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="Modal__close"
          onClick={onClose}
          aria-label="Cerrar"
          disabled={submitting}
        >
          ×
        </button>

        <h3 className="Modal__title">Añadir actividad</h3>
        <p className="Modal__desc">
          Escoge un tipo de actividad; el formulario se adapta a los campos de ese
          tipo.
        </p>

        {types === null ? (
          <p className="AddActivity__status">Cargando tipos…</p>
        ) : loadError ? (
          <p className="Modal__error">{loadError}</p>
        ) : types.length === 0 ? (
          <p className="AddActivity__status">
            No hay tipos de actividad. Crea uno desde el panel de administración.
          </p>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="FormField">
              <label className="FormLabel" htmlFor="aa-type">
                Tipo de actividad
              </label>
              <FlikSelect
                id="aa-type"
                variant="line"
                value={typeSlug}
                onChange={setTypeSlug}
                placeholder="Selecciona un tipo…"
                ariaLabel="Tipo de actividad"
                options={types.map((t) => ({
                  value: t.slug,
                  label: t.name,
                  icon: <ActivityTypeIcon activityType={t.slug} size={18} />,
                }))}
              />
            </div>

            {selectedType && (
              <>
                <div className="FormField">
                  <label className="FormLabel" htmlFor="aa-name">
                    Nombre
                  </label>
                  <input
                    id="aa-name"
                    ref={nameRef}
                    className="FormInput"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nombre de la actividad"
                  />
                </div>

                {[...selectedType.fields]
                  .sort(
                    (a, b) =>
                      (a.key === 'description' ? 1 : 0) -
                      (b.key === 'description' ? 1 : 0)
                  )
                  .map((field) => (
                  <ActivityField
                    key={field.key}
                    field={field}
                    value={values[field.key]}
                    onChange={(v) => setValue(field.key, v)}
                    disabled={field.key === 'frenchGrade' && Boolean(derivedMapping)}
                  />
                  ))}

                <div className="FormField">
                  <label className="FormLabel">Imágenes</label>
                  <div className="AddActivity__imgGrid">
                    {images.map((img) => (
                      <div key={img.url} className="AddActivity__imgItem">
                        <img src={img.url} alt="" />
                        <button
                          type="button"
                          className="AddActivity__imgDel"
                          aria-label="Quitar imagen"
                          disabled={submitting}
                          onClick={() => removeImage(img.url)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <label className="AddActivity__imgAdd">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        disabled={submitting}
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          e.target.value = '';
                          if (files.length) addImages(files);
                        }}
                      />
                      <span>+</span>
                    </label>
                  </div>
                </div>
              </>
            )}

            {error && <p className="Modal__error">{error}</p>}

            <div className="Modal__actions">
              <button
                type="button"
                className="Btn Btn--ghost"
                onClick={onClose}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="Btn Btn--primary"
                disabled={submitting || !selectedType}
              >
                {submitting ? 'Creando…' : 'Crear actividad'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** Renders a single dynamic field according to its FieldType. */
function ActivityField({ field, value, onChange, disabled = false }) {
  const id = `aa-field-${field.key}`;
  const label = `${field.label}${field.required ? ' *' : ''}`;

  if (field.type === 'BOOLEAN') {
    return (
      <label className="AddActivity__check" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{label}</span>
      </label>
    );
  }

  // Choice fields are fed from the options the backend sends for this field.
  // The backend's single-choice type is ENUM (with an `options` array).
  if (field.type === 'ENUM') {
    const options = normalizeOptions(field);
    return (
      <div className="FormField">
        <label className="FormLabel" htmlFor={id}>
          {label}
        </label>
        <FlikSelect
          id={id}
          variant="line"
          value={value ?? ''}
          onChange={onChange}
          options={options}
          disabled={disabled}
          placeholder={field.placeholder || 'Selecciona una opción'}
          ariaLabel={field.label}
        />
      </div>
    );
  }

  const common = {
    id,
    className: 'FormInput',
    value: value ?? '',
    placeholder: field.placeholder || '',
    disabled,
    onChange: (e) => onChange(e.target.value),
  };

  let input;
  if (field.key === 'description' || field.type === 'TEXT') {
    input = <textarea {...common} className="FormInput FormTextarea" rows={4} />;
  } else if (field.type === 'DATE') {
    input = <input {...common} type="date" />;
  } else if (field.type === 'DOUBLE' || field.type === 'INTEGER') {
    input = (
      <input
        {...common}
        type="number"
        step={field.type === 'INTEGER' ? '1' : 'any'}
        inputMode="decimal"
      />
    );
  } else {
    input = <input {...common} type="text" />;
  }

  return (
    <div className="FormField">
      <label className="FormLabel" htmlFor={id}>
        {label}
      </label>
      {input}
    </div>
  );
}

export default AddActivityModal;
